"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Link2, Share2 } from "lucide-react";

export default function PromoterLinkScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0F0B1F] text-white overflow-hidden">
      <div className="relative max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          {/* Copy */}
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
                {t("Unique promoter link", "Link único por promotor")}
              </div>
              <h2 className="text-4xl md:text-[56px] font-black tracking-[-0.03em] leading-[1.02] mb-6">
                {t("One link. ", "Un link. ")}
                <br />
                <span className="text-white/30">{t("Theirs alone.", "Suyo y de nadie más.")}</span>
              </h2>
              <p className="text-[16px] font-medium text-white/55 leading-relaxed mb-6">
                {t(
                  "Each promoter gets a unique sales link for each event to drop in Instagram bios, DMs, group chats. Every sale through that link is theirs — automatically tracked. No coupon codes to remember.",
                  "Cada promotor obtiene un link único de ventas por evento para meter en bio de Instagram, DMs, group chats. Cada venta por ese link es suya — trackeada automáticamente. Sin códigos que recordar."
                )}
              </p>

              <div className="inline-flex items-center gap-2.5 bg-white/[0.04] border border-white/10 px-4 py-3 rounded-full">
                <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: "rgba(244,114,182,0.15)" }}>
                  <Share2 className="w-3.5 h-3.5" style={{ color: "#F472B6" }} />
                </div>
                <div className="text-[13px] font-bold text-white">
                  {t("Instagram · TikTok · DMs · WhatsApp", "Instagram · TikTok · DMs · WhatsApp")}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Link card mockup */}
          <div className="md:col-span-7">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
            >
              {/* Mock promoter share card */}
              <div
                className="rounded-[24px] p-6 mb-4 relative overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 60%, #FB923C 100%)",
                }}
              >
                <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
                  backgroundImage: "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.4) 0%, transparent 50%)",
                }} />

                <div className="relative">
                  <div className="flex items-center gap-3 mb-5">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-[#0A0814] font-black text-[14px] bg-white"
                    >
                      S
                    </div>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-white/85">
                        {t("Sofia's link", "Link de Sofia")}
                      </div>
                      <div className="text-[14px] font-black text-white">NEON RUSH · Friday</div>
                    </div>
                    <div className="ml-auto bg-white/20 backdrop-blur-sm rounded-full px-2.5 py-1">
                      <span className="text-[9px] font-black uppercase tracking-wider text-white">15% cmsn</span>
                    </div>
                  </div>

                  <div className="bg-black/30 backdrop-blur-sm rounded-[12px] px-4 py-3 mb-4">
                    <div className="text-[8px] font-bold uppercase tracking-widest text-white/60 mb-1">URL</div>
                    <div
                      className="text-[14px] font-black text-white tracking-tight"
                      style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
                    >
                      useredy.com/buy/neon-rush?ref=<span style={{ color: "#FCA17D" }}>sofia</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: t("Sold via", "Vendido vía"), value: "87" },
                      { label: t("Commission", "Comisión"), value: "$522" },
                      { label: t("Status", "Estado"), value: t("Unpaid", "Pendiente") },
                    ].map((s) => (
                      <div key={s.label} className="text-center">
                        <div className="text-[8px] font-bold uppercase tracking-widest text-white/65 mb-0.5">
                          {s.label}
                        </div>
                        <div className="text-[15px] font-black text-white tabular-nums">{s.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* IG bio mockup */}
              <div className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[20px] p-5">
                <div className="text-[10px] font-black tracking-widest uppercase text-white/45 mb-3">
                  {t("Instagram bio (link in bio)", "Bio de Instagram (link in bio)")}
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-black"
                    style={{ background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)" }}
                  >
                    S
                  </div>
                  <div>
                    <div className="text-[13px] font-black text-white">@sofia.dallas</div>
                    <div className="text-[10px] text-white/55">14.2K followers</div>
                  </div>
                </div>
                <div className="bg-white/[0.04] border border-white/5 rounded-[10px] px-3 py-2 flex items-center gap-2">
                  <Link2 className="w-3.5 h-3.5" style={{ color: "#F472B6" }} />
                  <div className="text-[12px] font-bold text-white" style={{ fontFamily: "ui-monospace, monospace" }}>
                    🎉 NEON RUSH FRI · useredy.com/buy/neon-rush?ref=sofia
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
